import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Overlay, Modal, Header, Title, CloseButton, Content, Section,
  QueryPreview, QueryTitle, QueryText, Footer, ClearButton, CancelButton, SearchButton,
  SectionTitle, InputGroup, InputWrapper, Label, Input, CheckboxLabel,
} from './AdvancedSearchBuilder.styled';
import useVehicleData from './useVehicleData';
import useAdvancedSearchForm from './useAdvancedSearchForm';
import { DimensionsSection, VehicleSection, SeasonsSection, BrandsSection, PriceSection } from './sections';

const AdvancedSearchBuilder = ({ isVisible, onClose, onSearch, availableSeasons, availableBrands }) => {
  const { t } = useTranslation();
  const { query, handleChange, handleVehicleChange, handleSeasonToggle, handleBrandToggle, handleClear, buildQueryPreview } = 
    useAdvancedSearchForm(availableSeasons, availableBrands);

  // Pass vehicle data to the hook
  const vehicle = {
    vendor: query.vehicleVendor,
    model: query.vehicleModel,
    year: query.vehicleYear,
    version: query.vehicleVersion,
    fitment: query.vehicleFitment,
  };

  const {
    vendors,
    models,
    years,
    versions,
    fitments,
    resolvedEans,
    loadingVendors,
    loadingModels,
    loadingYears,
    loadingVersions,
    loadingFitments,
    loadingEans,
    clearAll,
  } = useVehicleData(isVisible, vehicle);

  const handleSubmit = () => {
    onSearch({ ...query, resolvedEans });
    onClose();
  };

  if (!isVisible) return null;

  return (
    <Overlay isVisible={isVisible} onClick={onClose}>
      <Modal isVisible={isVisible} onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title><i className="mdi mdi-tune" />{t('advancedSearch')}</Title>
          <CloseButton onClick={onClose}><i className="mdi mdi-close" /></CloseButton>
        </Header>

        <Content>
          <Section>
            <SectionTitle>{t('basicSearch')}</SectionTitle>
            <InputGroup>
              <InputWrapper>
                <Label>{t('catalogNumber')}</Label>
                <Input type="text" placeholder="npr. 12345ABC" value={query.catalogNumber} onChange={(e) => handleChange('catalogNumber', e.target.value)} />
              </InputWrapper>
            </InputGroup>
          </Section>

          <DimensionsSection width={query.width} profile={query.profile} rim={query.rim} onChange={handleChange} />
          
          <VehicleSection
            vehicle={vehicle}
            vendors={vendors}
            models={models}
            years={years}
            versions={versions}
            fitments={fitments}
            loadingVendors={loadingVendors}
            loadingModels={loadingModels}
            loadingYears={loadingYears}
            loadingVersions={loadingVersions}
            loadingFitments={loadingFitments}
            onChange={handleVehicleChange}
          />

          <SeasonsSection seasons={availableSeasons} selectedSeasons={query.selectedSeasons} onToggle={handleSeasonToggle} />
          <BrandsSection brands={availableBrands} selectedBrands={query.selectedBrands} onToggle={handleBrandToggle} />
          <PriceSection minPrice={query.minPrice} maxPrice={query.maxPrice} onChange={handleChange} />

          <Section>
            <CheckboxLabel checked={query.inStockOnly}>
              <input type="checkbox" checked={query.inStockOnly} onChange={(e) => handleChange('inStockOnly', e.target.checked)} />
              {t('onlyItemsInStock')}
            </CheckboxLabel>
          </Section>

          <QueryPreview>
            <QueryTitle>{t('queryPreview')}</QueryTitle>
            <QueryText>{buildQueryPreview({ vendors, models, years, versions, fitments })}</QueryText>
          </QueryPreview>
        </Content>

        <Footer>
          <ClearButton onClick={() => handleClear(clearAll)}><i className="mdi mdi-broom" />{t('clear')}</ClearButton>
          <div style={{ display: 'flex', gap: '12px' }}>
            <CancelButton onClick={onClose}>{t('cancel')}</CancelButton>
            <SearchButton onClick={handleSubmit} disabled={loadingEans}>
              <i className={loadingEans ? "mdi mdi-loading mdi-spin" : "mdi mdi-magnify"} />
              {loadingEans ? '...' : t('search')}
              {resolvedEans.length > 0 && !loadingEans && ` (${resolvedEans.length})`}
            </SearchButton>
          </div>
        </Footer>
      </Modal>
    </Overlay>
  );
};

AdvancedSearchBuilder.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  availableSeasons: PropTypes.arrayOf(PropTypes.object),
  availableBrands: PropTypes.arrayOf(PropTypes.object),
};

export default AdvancedSearchBuilder;
